import React, { useState } from 'react';
import Header from './Home/components/Header';
import Footer from './Home/components/Footer';
import { ArrowUpTrayIcon, CheckCircleIcon, ExclamationCircleIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useSEO } from '../hooks/useSEO';
import RelatedGuides from '../components/RelatedGuides';

// Import the validator - simple dynamic import for browser compatibility
let validator = null;

async function loadValidator() {
  if (validator) return validator;
  try {
    validator = await import('gltf-validator');
    return validator;
  } catch (error) {
    console.error('Failed to load gltf-validator:', error);
    return null;
  }
}

function GltfValidatorUpload() {
  const [file, setFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleSingleFile = (selectedFile) => {
    setValidationResult(null);
    setErrorMessage('');

    if (!selectedFile) {
      setFile(null);
      return;
    }

    const ext = selectedFile.name.toLowerCase().split('.').pop();
    if (ext !== 'gltf' && ext !== 'glb') {
      setErrorMessage('Please upload a .gltf or .glb file.');
      setFile(null);
      return;
    }

    const fileSizeMB = selectedFile.size / 1024 / 1024;
    setFile(selectedFile);
    setErrorMessage(`Selected file: ${selectedFile.name} (${fileSizeMB.toFixed(2)} MB)`);
  };

  const handleFileChange = (e) => {
    if (!e.target.files || e.target.files.length === 0) {
      handleSingleFile(null);
      return;
    }
    handleSingleFile(e.target.files[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      handleSingleFile(droppedFiles[0]);
    }
  };

  const getState = () => {
    if (!file) return 'idle';
    const ext = file.name.toLowerCase().split('.').pop();
    if (ext !== 'gltf' && ext !== 'glb') return 'error';
    return 'valid';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;
    const state = getState();
    if (state === 'error') return;

    setValidating(true);
    setValidationResult(null);
    
    try {
      const validatorModule = await loadValidator();
      if (!validatorModule) {
        throw new Error('Could not load validator');
      }

      const ext = file.name.toLowerCase().split('.').pop();
      let result;

      if (ext === 'gltf') {
        // For GLTF files, read as text
        const text = await file.text();
        result = await validatorModule.validateString(text);
      } else {
        // For GLB files, read as bytes
        const arrayBuffer = await file.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        result = await validatorModule.validateBytes(bytes);
      }

      // Process the result to match our expected format
      const processedResult = {
        uri: file.name,
        mimeType: file.type,
        validatorVersion: validatorModule.version || 'Unknown',
        validatedAt: new Date().toISOString(),
        issues: {
          numErrors: result.issues.numErrors || 0,
          numWarnings: result.issues.numWarnings || 0,
          numInfos: result.issues.numInfos || 0,
          numHints: result.issues.numHints || 0,
          messages: result.issues.messages || []
        },
        info: result.info || {}
      };

      setValidationResult(processedResult);
    } catch (err) {
      console.error('Validation error:', err);
      setErrorMessage(`Failed to validate file: ${err.message}`);
    } finally {
      setValidating(false);
    }
  };

  const downloadReport = () => {
    if (!validationResult) return;
    
    const blob = new Blob([JSON.stringify(validationResult, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${file.name.replace(/\.[^/.]+$/, '')}-validation-report.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const state = getState();
  const isError = state === 'error';
  const isValid = state === 'valid';

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 0: return <ExclamationCircleIcon className="h-5 w-5 text-danger" />;
      case 1: return <ExclamationTriangleIcon className="h-5 w-5 text-warning" />;
      case 2: return <InformationCircleIcon className="h-5 w-5 text-info" />;
      case 3: return <InformationCircleIcon className="h-5 w-5 text-muted" />;
      default: return <InformationCircleIcon className="h-5 w-5 text-muted" />;
    }
  };

  const getSeverityLabel = (severity) => {
    switch (severity) {
      case 0: return 'Error';
      case 1: return 'Warning';
      case 2: return 'Info';
      case 3: return 'Hint';
      default: return 'Unknown';
    }
  };

  return (
    <section className="bg-background" id="file-upload">
      <div className="max-w-5xl w-full mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="lg:text-center">
          <h2 className="text-base text-accent font-semibold tracking-wide uppercase">
            Try it out
          </h2>
          <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-foreground sm:text-4xl">
            Validate your GLTF files
          </p>
          <p className="mt-4 max-w-2xl text-xl text-muted-foreground/90 lg:mx-auto">
            Free, comprehensive validation powered by the official Khronos Group validator.
          </p>
        </div>

        <div className="mt-10">
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="space-y-8">
              <div className="mt-6 space-y-6">
                <div>
                  <label htmlFor="file-upload" className="block text-sm font-medium text-muted-foreground mb-2">
                    Upload your GLTF or GLB file
                  </label>
                  <div
                    className={`group flex justify-center rounded-xl border-2 border-dashed p-12 transition-all duration-200 cursor-pointer bg-background-elevated ${
                      isDragging ? 'border-accent bg-accent/10' : isError ? 'border-danger' : isValid ? 'border-success' : 'border-border/60 hover:border-border'
                    }`}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onClick={() => document.getElementById('gltf-validator-input').click()}
                  >
                    <div className="text-center pointer-events-none">
                      {!file && <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-muted-foreground/70" />}
                      {isValid && <CheckCircleIcon className="mx-auto h-12 w-12 text-success" />}
                      {isError && <ExclamationCircleIcon className="mx-auto h-12 w-12 text-danger" />}
                      <div className="mt-4 flex text-sm leading-6 text-muted-foreground justify-center">
                        <label htmlFor="gltf-validator-input" className="relative cursor-pointer rounded-lg font-medium text-accent hover:text-accent/80 focus:outline-none pointer-events-auto transition-colors duration-200">
                          <span>Choose a file</span>
                          <input id="gltf-validator-input" name="file-upload" type="file" className="sr-only" onChange={handleFileChange} accept=".gltf,.glb" />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-muted-foreground/80 mt-2">GLTF and GLB files supported</p>
                      {errorMessage && (
                        <p className={`mt-2 text-sm ${isError ? 'text-danger' : 'text-success'}`}>{errorMessage}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <button
                type="submit"
                className="inline-flex justify-center py-3 px-6 border border-transparent text-sm font-semibold rounded-lg text-primary-foreground bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent/50 transition-all duration-200 disabled:opacity-50 shadow-sm"
                disabled={!file || isError || validating}
              >
                {validating ? 'Validating…' : 'Validate now'}
              </button>
            </div>
          </form>
        </div>

        {validationResult && (
          <div className="mt-12 space-y-8">
            {/* Validation Summary */}
            <div className="bg-background-elevated rounded-xl border border-border/60 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">Validation Summary</h3>
                <button
                  onClick={downloadReport}
                  className="text-sm text-accent hover:text-accent/80 border border-accent/60 hover:border-accent rounded-lg px-3 py-1.5 transition-all duration-200"
                >
                  Download Report
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-danger">{validationResult.issues.numErrors}</div>
                  <div className="text-sm text-muted-foreground/90">Errors</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-warning">{validationResult.issues.numWarnings}</div>
                  <div className="text-sm text-muted-foreground/90">Warnings</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-info">{validationResult.issues.numInfos}</div>
                  <div className="text-sm text-muted-foreground/90">Info</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-muted-foreground">{validationResult.issues.numHints}</div>
                  <div className="text-sm text-muted-foreground/90">Hints</div>
                </div>
              </div>

              <div className={`text-center py-2 px-4 rounded-lg ${
                validationResult.issues.numErrors === 0 ? 'bg-success/10 text-success border border-success/30' : 'bg-danger/10 text-danger border border-danger/30'
              }`}>
                {validationResult.issues.numErrors === 0 ? 'Valid glTF Asset' : 'Invalid glTF Asset'}
              </div>
            </div>

            {/* Issues List */}
            {validationResult.issues.messages.length > 0 && (
              <div className="bg-background-elevated rounded-xl border border-border/60 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-foreground mb-4">Issues</h3>
                <div className="space-y-3">
                  {validationResult.issues.messages.map((issue, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 bg-background-soft rounded-lg border border-border/40">
                      {getSeverityIcon(issue.severity)}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className={`text-sm font-medium ${
                            issue.severity === 0 ? 'text-danger' :
                            issue.severity === 1 ? 'text-warning' :
                            issue.severity === 2 ? 'text-info' : 'text-muted-foreground'
                          }`}>
                            {getSeverityLabel(issue.severity)}
                          </span>
                          <span className="text-xs text-muted-foreground/80">{issue.code}</span>
                        </div>
                        <p className="text-sm text-muted-foreground/90 mt-1">{issue.message}</p>
                        {issue.pointer && (
                          <p className="text-xs text-muted-foreground/80 mt-1">Location: {issue.pointer}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Asset Statistics */}
            {validationResult.info && (
              <div className="bg-background-elevated rounded-xl border border-border/60 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-foreground mb-4">Asset Statistics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground/90">Version</div>
                    <div className="text-foreground">{validationResult.info.version}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground/90">Generator</div>
                    <div className="text-foreground">{validationResult.info.generator || 'Unknown'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground/90">Vertices</div>
                    <div className="text-foreground">{validationResult.info.totalVertexCount?.toLocaleString() || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground/90">Triangles</div>
                    <div className="text-foreground">{validationResult.info.totalTriangleCount?.toLocaleString() || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground/90">Draw Calls</div>
                    <div className="text-foreground">{validationResult.info.drawCallCount || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground/90">Primitives</div>
                    <div className="text-foreground">{validationResult.info.primitivesCount || 'N/A'}</div>
                  </div>
                  {validationResult.info.extensionsUsed && validationResult.info.extensionsUsed.length > 0 && (
                    <div className="md:col-span-2 lg:col-span-3">
                      <div className="text-sm text-muted-foreground/90 mb-2">Extensions Used</div>
                      <div className="flex flex-wrap gap-2">
                        {validationResult.info.extensionsUsed.map((ext, index) => (
                          <span key={index} className="text-xs bg-accent/10 text-accent px-2 py-1 rounded-lg border border-accent/20">
                            {ext}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export default function GltfValidatorPage() {
  useSEO({
    title: 'glTF & GLB Validator Online – Official Khronos glTF-Validator',
    description: 'Validate GLB and glTF files online with the official Khronos glTF-Validator. Runs in your browser, no upload or sign-up. See errors, warnings and mesh stats.',
    keywords: 'GLTF validator, GLB validation, 3D model validation, Khronos Group validator, GLTF specification',
    canonical: 'https://compress-glb.com/gltf-validator',
    ogImage: '/images/og/gltf-validator.png',
    ogTitle: 'glTF & GLB Validator Online – Official Khronos glTF-Validator',
    ogDescription: 'Validate your GLTF and GLB files against the official specification using the Khronos Group validator.',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "GLTF Validator",
      "description": "Free online GLTF and GLB file validator using official Khronos Group validation",
      "url": "https://compress-glb.com/gltf-validator/",
      "applicationCategory": "UtilityApplication",
      "operatingSystem": "Web Browser",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      }
    }
  });

  return (
    <div className="bg-background text-foreground min-h-screen flex flex-col">
      <Header />

      <main className="flex-grow">
        <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            <span className="block">GLTF Validator</span>
            <span className="block text-accent">Comprehensive & free</span>
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-xl text-muted-foreground/90">
            Validate your GLTF and GLB files against the official specification. Powered by the Khronos Group validator.
          </p>
          <div className="mt-8 max-w-md mx-auto sm:flex sm:justify-center md:mt-8">
            <a href="#file-upload" className="w-full flex items-center justify-center px-4 py-3 border border-accent text-base font-medium rounded-lg text-accent hover:bg-accent/10 md:py-4 md:text-lg md:px-6 transition-all duration-200">
              Try for free
            </a>
          </div>
        </div>

        <GltfValidatorUpload />

        <RelatedGuides
          className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16"
          title="Related guides"
          slugs={['what-is-a-glb-file', 'reduce-glb-file-size', 'threejs-load-glb']}
        />
      </main>

      <Footer />
    </div>
  );
} 